/**
 * Make-up Lesson Credit Utilities
 * Domain logic for handling make-up lesson credits
 */

import {
  Lesson,
  MakeupCredit,
  type Academy,
  type MakeupCreditExpiryMode,
} from '../types';

/** Input for guardian-facing copy about this academy’s make-up rules */
export type MakeupAcademyPolicyInput = Pick<
  Academy,
  | 'makeupLessonEnabled'
  | 'makeupNotificationDeadlineHours'
  | 'makeupCreditExpiryDays'
  | 'makeupCreditExpiryMode'
>;

export interface MakeupAcademyPolicySummary {
  /** Whether the academy offers make-up credits */
  enabled: boolean;
  /** Short lines for UI (e.g. bullet list) */
  lines: string[];
}

/**
 * Human-readable summary of make-up policy for students/guardians (dashboard copy).
 */
export function getMakeupAcademyPolicySummary(academy: MakeupAcademyPolicyInput): MakeupAcademyPolicySummary {
  const enabled = academy.makeupLessonEnabled ?? false;
  const deadlineH = academy.makeupNotificationDeadlineHours ?? 24;
  const expiryDays = academy.makeupCreditExpiryDays ?? 30;
  const mode = academy.makeupCreditExpiryMode ?? 'days';

  if (!enabled) {
    return {
      enabled: false,
      lines: ['This academy does not offer make-up credits.'],
    };
  }

  const lines: string[] = [
    `Report an absence at least ${deadlineH} hours before the lesson starts to earn a credit (when eligible).`,
  ];

  if (mode === 'end_of_term') {
    lines.push(
      `For term group lessons, credits usually expire at the end of that term (with a short grace period). Private or camp lessons without a term use a ${expiryDays}-day window from when the credit is earned.`
    );
  } else {
    lines.push(`Each credit expires ${expiryDays} days after it is earned.`);
  }

  return { enabled: true, lines };
}

/**
 * Check if a student is eligible to earn a make-up credit for reporting absence
 * Eligibility is based on reporting the absence within the deadline window before the lesson
 *
 * @param absenceReportedAt - When the absence was reported (now)
 * @param lessonStartTime - When the lesson was scheduled to start
 * @param deadlineHours - How many hours before lesson start they can report (e.g., 24)
 * @returns true if within the deadline window
 */
export function isEligibleForMakeupCredit(
  absenceReportedAt: Date,
  lessonStartTime: Date,
  deadlineHours: number
): boolean {
  // Check if lesson has already started
  if (absenceReportedAt >= lessonStartTime) {
    return false;
  }

  // Student must report at least deadlineHours before the lesson to earn a credit
  const millisecondsBeforeLesson = lessonStartTime.getTime() - absenceReportedAt.getTime();
  const hoursBeforeLesson = millisecondsBeforeLesson / (1000 * 60 * 60);

  return hoursBeforeLesson >= deadlineHours;
}

/**
 * Check if a lesson is compatible with a make-up credit.
 *
 * Compatibility rules (in order of precedence):
 * 1. Same lessonType (group/private/camp), **except** `group` and `camp` may match each other (term-style group
 *    vs holiday camp) when skill/age rules below pass.
 * 2. Same skillLevel when the credit specifies one (`any` on credit or lesson is a wildcard — open to all levels)
 * 3. Age group matching:
 *    - When both credit and lesson have an ageGroupId (academy-defined group):
 *      they must be the same group.
 *    - Otherwise fall back to legacy ageGroup/ballColor logic:
 *      adult credit → adult or 'all' lesson; junior → junior or 'all'; 'all' → any.
 *      For juniors: if credit has ballColor set, lesson must have same ballColor.
 *
 * @param credit - The make-up credit to check
 * @param lesson - The lesson to check compatibility with
 * @returns true if lesson is compatible with the credit
 */
/** Compatibility fields copied from a lesson onto a make-up credit at earn time (and refreshed on lesson update). */
export type MakeupCreditCompatibilitySnapshot = Pick<
  MakeupCredit,
  'lessonType' | 'skillLevel' | 'ageGroup' | 'ballColor' | 'ageGroupId' | 'academySportId'
>;

/** Build the compatibility snapshot stored on credits from a lesson's current attributes. */
export function getMakeupCreditCompatibilitySnapshotFromLesson(
  lesson: Pick<
    Lesson,
    'lessonType' | 'skillLevel' | 'ageGroup' | 'ballColor' | 'ageGroupId' | 'academySportId'
  >
): MakeupCreditCompatibilitySnapshot {
  return {
    lessonType: lesson.lessonType,
    skillLevel: lesson.skillLevel,
    ageGroup: lesson.ageGroup,
    ballColor: lesson.ballColor,
    ageGroupId: lesson.ageGroupId,
    academySportId: lesson.academySportId,
  };
}

/** True when a credit's stored lesson type can redeem the target lesson type (group ↔ camp allowed). */
export function areLessonTypesCompatibleForMakeup(creditType: string, lessonType: string): boolean {
  if (creditType === lessonType) return true;
  // Group term lessons and camps share junior pathway / ball-color rules; allow cross-redemption.
  return (
    (creditType === 'group' && lessonType === 'camp') || (creditType === 'camp' && lessonType === 'group')
  );
}

/** True when credit and lesson skill levels are compatible (`any` is a wildcard on either side). */
export function areSkillLevelsCompatibleForMakeup(
  creditSkillLevel?: string,
  lessonSkillLevel?: string
): boolean {
  if (!creditSkillLevel || creditSkillLevel === 'any') {
    return true;
  }
  if (!lessonSkillLevel || lessonSkillLevel === 'any') {
    return true;
  }
  return creditSkillLevel === lessonSkillLevel;
}

export function isLessonCompatibleWithCredit(
  credit: MakeupCredit,
  lesson: Lesson
): boolean {
  if (
    credit.academySportId &&
    lesson.academySportId &&
    credit.academySportId !== lesson.academySportId
  ) {
    return false;
  }

  if (!areLessonTypesCompatibleForMakeup(credit.lessonType, lesson.lessonType)) {
    return false;
  }

  if (!areSkillLevelsCompatibleForMakeup(credit.skillLevel, lesson.skillLevel)) {
    return false;
  }

  // Academy-defined age group takes precedence when the credit has one
  if (credit.ageGroupId) {
    return lesson.ageGroupId === credit.ageGroupId;
  }

  // Legacy ageGroup/ballColor matching (AU Hot Shots and simple juniors/adults split)
  const creditAgeGroup = credit.ageGroup ?? 'all';
  const lessonAgeGroup = lesson.ageGroup ?? 'all';
  if (creditAgeGroup === 'adults') {
    if (lessonAgeGroup === 'juniors') return false;
  }
  if (creditAgeGroup === 'juniors') {
    if (lessonAgeGroup === 'adults') return false;
    // For juniors: if original lesson had ball color, replacement must match
    if (credit.ballColor && lesson.ballColor !== credit.ballColor) return false;
  }

  return true;
}

/**
 * Filter lessons to find those compatible with a make-up credit
 * (see {@link isLessonCompatibleWithCredit} for type/skill/age rules).
 *
 * @param credit - The make-up credit
 * @param availableLessons - Array of lessons to filter
 * @returns Array of compatible lessons
 */
export function findCompatibleLessons(
  credit: MakeupCredit,
  availableLessons: Lesson[]
): Lesson[] {
  return availableLessons.filter((lesson) => isLessonCompatibleWithCredit(credit, lesson));
}

/** Minimum grace period (days) when using end-of-term expiry so credits are not immediately unusable. */
export const MAKEUP_CREDIT_END_OF_TERM_MIN_GRACE_DAYS = 7;

export interface CalculateCreditExpiryDateOptions {
  mode?: MakeupCreditExpiryMode;
  /** Term end date when mode is end_of_term */
  termEndDate?: Date;
}

/**
 * Calculate credit expiry date based on creation date and academy policy.
 *
 * - **days** (default): createdAt + expiryDays
 * - **end_of_term**: max(termEndDate, createdAt + min grace days) when termEndDate is provided; otherwise same as days mode
 */
export function calculateCreditExpiryDate(
  createdAt: Date,
  expiryDays: number,
  options?: CalculateCreditExpiryDateOptions
): Date {
  const mode = options?.mode ?? 'days';
  if (mode === 'end_of_term' && options?.termEndDate) {
    const endOfTermMs = new Date(options.termEndDate).getTime();
    const minGrace = new Date(createdAt);
    minGrace.setDate(minGrace.getDate() + MAKEUP_CREDIT_END_OF_TERM_MIN_GRACE_DAYS);
    const minGraceMs = minGrace.getTime();
    return new Date(Math.max(endOfTermMs, minGraceMs));
  }
  const expiryDate = new Date(createdAt);
  expiryDate.setDate(expiryDate.getDate() + expiryDays);
  return expiryDate;
}

/**
 * Resolve make-up credit expiry from academy settings and optional term end (group lessons).
 * Private/camp lessons with no term: always uses days-based expiry (pass termEndDate undefined).
 */
export function resolveMakeupCreditExpiresAt(params: {
  academy: Pick<Academy, 'makeupCreditExpiryMode' | 'makeupCreditExpiryDays'>;
  createdAt: Date;
  termEndDate?: Date | null;
}): Date {
  const { academy, createdAt, termEndDate } = params;
  const expiryDays = academy.makeupCreditExpiryDays ?? 30;
  const mode = academy.makeupCreditExpiryMode ?? 'days';
  if (mode === 'end_of_term' && termEndDate) {
    return calculateCreditExpiryDate(createdAt, expiryDays, {
      mode: 'end_of_term',
      termEndDate,
    });
  }
  return calculateCreditExpiryDate(createdAt, expiryDays);
}

/**
 * Check if a make-up credit has expired
 *
 * @param credit - The credit to check
 * @param currentDate - Current date (defaults to now)
 * @returns true if the credit has expired
 */
export function isCreditExpired(credit: MakeupCredit, currentDate: Date = new Date()): boolean {
  return currentDate >= credit.expiresAt;
}

/**
 * Check if a make-up credit is available (not yet used or expired)
 *
 * @param credit - The credit to check
 * @param currentDate - Current date (defaults to now)
 * @returns true if the credit is available for use
 */
export function isCreditAvailable(credit: MakeupCredit, currentDate: Date = new Date()): boolean {
  return credit.status === 'available' && !isCreditExpired(credit, currentDate);
}

/**
 * Get the number of days until a credit expires
 *
 * @param credit - The credit to check
 * @param currentDate - Current date (defaults to now)
 * @returns Number of days until expiry (negative if already expired)
 */
export function getDaysUntilCreditExpires(credit: MakeupCredit, currentDate: Date = new Date()): number {
  const millisecondsUntilExpiry = credit.expiresAt.getTime() - currentDate.getTime();
  const daysUntilExpiry = millisecondsUntilExpiry / (1000 * 60 * 60 * 24);
  return Math.ceil(daysUntilExpiry);
}
